// ═══════════════════════════════════════════════════════════════════════
// WarehouseInventoryPage — Sprint 2026-Q1d / Step ⑦ · Pass 1 · Report ④
// ═══════════════════════════════════════════════════════════════════════
// NOT a P&L report. Operational stock-keeping view, one row per
// warehouse, merging the inventory block (Opening · Purchase · Issue ·
// Return · Closing · Closing Value) with the asset block (Opening NBV ·
// Additions · Retirements · Closing NBV · Monthly Depreciation), plus
// the headline "Capital Employed" tile = Closing Value + Closing NBV.
// ═══════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { PageHeader, Card, EmptyState } from "@/components/ui-helpers";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { useFormTab } from "@/hooks/useFormTab";
import {
  Filter as FilterIcon, RefreshCw, Download, Search, X,
  CheckCircle2, AlertTriangle, ArrowRight, FileText, Warehouse,
  Package, Cpu,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import UserLabel, { useUserDirectory } from "@/components/UserLabel";

const currentFy = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = d.getMonth();
  const startYear = m >= 3 ? y : y - 1;
  return {
    from_period: `${startYear}-04`,
    to_period:   `${(startYear + 1).toString().padStart(4, "0")}-03`,
  };
};

const DRILL_SOURCES = [
  { key: "stock_entries",            label: "Stock Movements (IN / OUT / RETURN)" },
  { key: "inventory_batches",        label: "Closing Inventory Batches" },
  { key: "depreciation_snapshots",   label: "Depreciation Snapshots" },
  { key: "depreciation_adjustments", label: "Depreciation Adjustments" },
  { key: "asset_additions",          label: "Asset Additions (purchase in window)" },
  { key: "asset_retirements",        label: "Asset Retirements / Scrap" },
];


export default function WarehouseInventoryPage() {
  useUserDirectory();
  const openFormTab = useFormTab();
  const [{ from_period, to_period }, setRange] = useState(currentFy());
  const [filters, setFilters] = useState({
    branch_id: "", warehouse_id: "",
    asset_category: "", item_category: "",
    include_inactive: false,
  });
  const [branches, setBranches] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [reconcile, setReconcile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/branches", { params: { limit: 500 } })
      .then(r => setBranches(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setBranches([]));
    api.get("/warehouses", { params: { limit: 500 } })
      .then(r => setWarehouses(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(() => setWarehouses([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        from_period, to_period,
        include_inactive: filters.include_inactive,
      };
      if (filters.branch_id)      params.branch_id      = filters.branch_id;
      if (filters.warehouse_id)   params.warehouse_id   = filters.warehouse_id;
      if (filters.asset_category) params.asset_category = filters.asset_category;
      if (filters.item_category)  params.item_category  = filters.item_category;
      const [res, rec] = await Promise.all([
        api.get("/reports/warehouse-inventory/_summary", { params }),
        api.get("/reports/warehouse-inventory/_reconcile", {
          params: {
            from_period, to_period,
            asset_category: filters.asset_category || undefined,
            item_category:  filters.item_category  || undefined,
          },
        }),
      ]);
      setRows(res.data?.rows || []);
      setTotals(res.data?.totals || {});
      setReconcile(rec.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Warehouse Inventory Report.");
    } finally {
      setLoading(false);
    }
  }, [from_period, to_period, filters]);

  useEffect(() => { load(); }, [load]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.warehouse_code || "").toLowerCase().includes(q) ||
      (r.warehouse_name || "").toLowerCase().includes(q) ||
      (r.branch_name    || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  // Filter the warehouse dropdown to selected branch (UX hygiene)
  const warehousesForBranch = useMemo(() => {
    if (!filters.branch_id) return warehouses;
    return warehouses.filter(w => w.branch_id === filters.branch_id);
  }, [warehouses, filters.branch_id]);

  const openDrill = (row, source) => {
    const meta = DRILL_SOURCES.find(s => s.key === source);
    openFormTab({
      path: `/reports/warehouse-inventory/drill/${row.warehouse_id}/${source}`,
      label: `${meta?.label || source} · ${row.warehouse_code || row.warehouse_name}`,
      parent: "/reports/warehouse-inventory",
      render: ({ close }) => (
        <WarehouseInventoryDrillTab
          row={row}
          source={source}
          from_period={from_period}
          to_period={to_period}
          asset_category={filters.asset_category}
          item_category={filters.item_category}
          onClose={close}
        />
      ),
    });
  };

  const exportCsv = async () => {
    try {
      const params = new URLSearchParams({
        from_period, to_period,
        include_inactive: String(filters.include_inactive),
      });
      if (filters.branch_id)      params.append("branch_id",      filters.branch_id);
      if (filters.warehouse_id)   params.append("warehouse_id",   filters.warehouse_id);
      if (filters.asset_category) params.append("asset_category", filters.asset_category);
      if (filters.item_category)  params.append("item_category",  filters.item_category);
      const resp = await api.get(`/reports/warehouse-inventory/export.csv?${params}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `warehouse-inventory-${from_period}_${to_period}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded.");
    } catch {
      toast.error("Failed to export CSV.");
    }
  };

  const reconcilePass = reconcile?.checks?.every(c => c.status === "PASS");

  return (
    <div className="space-y-6 p-6" data-testid="warehouse-inventory-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Step ⑦ · Pass 1 · Report ④ · Stock-keeping View"
        title="Warehouse Inventory Cost Report"
        subtitle="What's lying in each warehouse, what assets are parked there, and how much capital is locked inside. Stock flows from Stock Entries; Asset NBV from immutable depreciation snapshots."
        actions={
          <div className="flex items-center gap-2">
            <button
              data-testid="warehouse-inventory-refresh-btn"
              onClick={load}
              disabled={loading}
              className="text-xs uppercase tracking-wider font-bold px-3 py-2 border border-slate-300 hover:bg-slate-50">
              <RefreshCw size={12} className={`inline mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button
              data-testid="warehouse-inventory-export-csv-btn"
              onClick={exportCsv}
              className="text-xs uppercase tracking-wider font-bold px-3 py-2 bg-emerald-700 hover:bg-emerald-800 text-white">
              <Download size={12} className="inline mr-1" /> Export CSV
            </button>
          </div>
        }
      />

      {/* Filter bar */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <FilterIcon size={14} className="text-slate-500" />
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Filters</div>
        </div>
        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-3">
          <FilterField label="Period From">
            <input
              data-testid="warehouse-inventory-filter-from"
              type="month"
              value={from_period}
              onChange={e => setRange(r => ({ ...r, from_period: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Period To">
            <input
              data-testid="warehouse-inventory-filter-to"
              type="month"
              value={to_period}
              onChange={e => setRange(r => ({ ...r, to_period: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Branch">
            <select
              data-testid="warehouse-inventory-filter-branch"
              value={filters.branch_id}
              onChange={e => setFilters(f => ({ ...f, branch_id: e.target.value, warehouse_id: "" }))}
              className={inputCls}>
              <option value="">All Branches</option>
              {branches.map(b =>
                <option key={b.id} value={b.id}>{b.branch_code} — {b.branch_name}</option>
              )}
            </select>
          </FilterField>
          <FilterField label="Warehouse">
            <select
              data-testid="warehouse-inventory-filter-warehouse"
              value={filters.warehouse_id}
              onChange={e => setFilters(f => ({ ...f, warehouse_id: e.target.value }))}
              className={inputCls}>
              <option value="">All Warehouses</option>
              {warehousesForBranch.map(w =>
                <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>
              )}
            </select>
          </FilterField>
          <FilterField label="Asset Category">
            <input
              data-testid="warehouse-inventory-filter-asset-category"
              type="text"
              placeholder="e.g. IT, MEP, Vehicle…"
              value={filters.asset_category}
              onChange={e => setFilters(f => ({ ...f, asset_category: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Item Category">
            <input
              data-testid="warehouse-inventory-filter-item-category"
              type="text"
              placeholder="e.g. Cable, PPE, Tools…"
              value={filters.item_category}
              onChange={e => setFilters(f => ({ ...f, item_category: e.target.value }))}
              className={inputCls}
            />
          </FilterField>
          <FilterField label="Visibility">
            <label className="flex items-center gap-2 text-xs h-[34px]">
              <input
                data-testid="warehouse-inventory-filter-include-inactive"
                type="checkbox"
                checked={filters.include_inactive}
                onChange={e => setFilters(f => ({ ...f, include_inactive: e.target.checked }))}
                className="h-4 w-4"
              />
              Include inactive warehouses
            </label>
          </FilterField>
        </div>
      </Card>

      {/* KPI tiles */}
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile icon={<Package size={14} />}  label="Total Inventory Value"  value={totals.closing_value} tone="emerald" />
        <KpiTile icon={<Cpu size={14} />}      label="Total Asset Net Block"  value={totals.closing_nbv} tone="indigo" />
        <KpiTile icon={<ArrowRight size={14} />} label="Monthly Depreciation" value={totals.monthly_depreciation} tone="amber" />
        <KpiTile icon={<Warehouse size={14} />} label="Total Capital Employed" value={totals.capital_employed} tone="slate-bold" />
      </div>

      {/* Reconciliation badge */}
      {reconcile && (
        <Card className={`p-4 border ${reconcilePass ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}>
          <div className="flex items-start gap-3" data-testid="warehouse-inventory-reconcile-banner">
            {reconcilePass
              ? <CheckCircle2 size={18} className="text-emerald-700 flex-none mt-0.5" />
              : <AlertTriangle size={18} className="text-amber-700 flex-none mt-0.5" />}
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-700">
                Reconciliation — Warehouse Roll-up vs Source Registers
              </div>
              <div className="font-display text-base font-black text-slate-900 mt-0.5">
                {reconcilePass
                  ? "All 3 checks PASS — warehouse totals tie to the inventory ledger, asset register and depreciation source."
                  : `${reconcile.checks.filter(c => c.status === "FAIL").length} of ${reconcile.checks.length} check(s) failing.`}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 mt-2">
                {reconcile.checks.map((c, i) => (
                  <div key={i}
                       data-testid={`warehouse-inventory-reconcile-${c.check.toLowerCase().replace(/[^a-z]/g, "-")}`}
                       className={`text-xs px-2 py-1 rounded ${c.status === "PASS"
                                    ? "bg-emerald-100 text-emerald-900"
                                    : "bg-amber-100 text-amber-900"}`}>
                    <div className="font-bold">{c.status} · {c.check}</div>
                    {c.status === "FAIL" && (
                      <div className="text-[10px] mt-0.5">
                        Δ ₹{c.delta.toLocaleString("en-IN")} (rolled: ₹{c.rolled_up.toLocaleString("en-IN")} · source: ₹{c.source.toLocaleString("en-IN")})
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {!reconcilePass && (
                <div className="text-[11px] text-slate-600 mt-2 italic">
                  Drift here is usually orphan batches / assets / snapshots whose warehouse_id is blank. The Step ⑧ Financial Integrity Dashboard surfaces drill-down to fix.
                </div>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="warehouse-inventory-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search warehouse code / name / branch…"
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none"
          />
        </div>
        <div className="text-xs text-slate-500">
          {filteredRows.length} of {rows.length} warehouses
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <Card className="p-12 text-center text-slate-500">Loading Warehouse Inventory Report…</Card>
      ) : rows.length === 0 ? (
        <EmptyState title="No warehouses in this view"
                    hint="Try adjusting the filters or toggle 'Include inactive'." />
      ) : filteredRows.length === 0 ? (
        <EmptyState title="No matches" hint="Clear the search to see all warehouses." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="warehouse-inventory-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>
                  <th rowSpan={2} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider p-2 align-bottom border-r border-slate-200">Warehouse</th>
                  <th colSpan={6} className="text-center text-[10px] font-bold text-emerald-700 uppercase tracking-wider py-1.5 px-2 bg-emerald-50/40 border-r border-slate-200">
                    <Package size={11} className="inline mr-1" /> Inventory
                  </th>
                  <th colSpan={5} className="text-center text-[10px] font-bold text-indigo-700 uppercase tracking-wider py-1.5 px-2 bg-indigo-50/40 border-r border-slate-200">
                    <Cpu size={11} className="inline mr-1" /> Assets
                  </th>
                  <th rowSpan={2} className="text-right text-[10px] font-bold text-slate-700 uppercase tracking-wider p-2 align-bottom bg-slate-100">Capital Employed</th>
                </tr>
                <tr>
                  {/* Inventory cols */}
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Opening</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Purchase</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Issue</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Return</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Closing Qty</th>
                  <th className="text-right text-[10px] font-bold text-emerald-700 uppercase p-2 border-r border-slate-200">Closing ₹</th>
                  {/* Asset cols */}
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Open NBV</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Additions</th>
                  <th className="text-right text-[10px] font-bold text-slate-500 uppercase p-2">Scrap</th>
                  <th className="text-right text-[10px] font-bold text-indigo-700 uppercase p-2">Close NBV</th>
                  <th className="text-right text-[10px] font-bold text-amber-700 uppercase p-2 border-r border-slate-200">Depreciation</th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(r => (
                  <tr key={r.warehouse_id}
                      data-testid={`warehouse-inventory-row-${r.warehouse_id}`}
                      className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="p-2 border-r border-slate-100">
                      <div className="font-mono font-bold text-[#F97316]">{r.warehouse_code || "—"}</div>
                      <div className="text-[11px] text-slate-700 truncate max-w-[200px]">{r.warehouse_name}</div>
                      <div className="text-[10px] text-slate-400">{r.branch_name}</div>
                    </td>
                    <td className="p-2 text-right tabular-nums text-slate-600">{r.opening_qty || "—"}</td>
                    <td className="p-2 text-right tabular-nums text-emerald-700">{r.purchase_qty || "—"}</td>
                    <td className="p-2 text-right tabular-nums text-amber-700">{r.issue_qty || "—"}</td>
                    <td className="p-2 text-right tabular-nums text-emerald-700">{r.return_qty || "—"}</td>
                    <td className="p-2 text-right tabular-nums font-bold">{r.closing_qty || "—"}</td>
                    <DrillCell value={r.closing_value} onClick={() => r.closing_qty && openDrill(r, "inventory_batches")} count={r.closing_qty} tone="emerald" />
                    <td className="p-2 text-right tabular-nums text-slate-600">{r.opening_nbv ? formatINR(r.opening_nbv) : "—"}</td>
                    <DrillCell value={r.additions} onClick={() => r._counts.additions && openDrill(r, "asset_additions")} count={r._counts.additions} tone="emerald" />
                    <DrillCell value={r.retirements} onClick={() => r._counts.retirements && openDrill(r, "asset_retirements")} count={r._counts.retirements} tone="rose" />
                    <td className="p-2 text-right tabular-nums font-bold text-indigo-700">{r.closing_nbv ? formatINR(r.closing_nbv) : "—"}</td>
                    <DrillCell value={r.monthly_depreciation} onClick={() => r._counts.snapshots && openDrill(r, "depreciation_snapshots")} count={r._counts.snapshots + r._counts.adjustments} tone="amber" prefix="-" />
                    <td className="p-2 text-right tabular-nums font-black text-slate-900 bg-slate-50/40">
                      {r.capital_employed ? formatINR(r.capital_employed) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-100 border-t-2 border-slate-300 font-black">
                <tr data-testid="warehouse-inventory-totals-row">
                  <td className="p-2 text-[10px] uppercase tracking-wider border-r border-slate-200">Totals ({totals.warehouse_count})</td>
                  <td className="p-2 text-right tabular-nums">{totals.opening_qty || "—"}</td>
                  <td className="p-2 text-right tabular-nums">{totals.purchase_qty || "—"}</td>
                  <td className="p-2 text-right tabular-nums">{totals.issue_qty || "—"}</td>
                  <td className="p-2 text-right tabular-nums">{totals.return_qty || "—"}</td>
                  <td className="p-2 text-right tabular-nums">{totals.closing_qty || "—"}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-800 border-r border-slate-200">{formatINR(totals.closing_value || 0)}</td>
                  <td className="p-2 text-right tabular-nums">{formatINR(totals.opening_nbv || 0)}</td>
                  <td className="p-2 text-right tabular-nums text-emerald-700">{formatINR(totals.additions || 0)}</td>
                  <td className="p-2 text-right tabular-nums text-rose-700">{formatINR(totals.retirements || 0)}</td>
                  <td className="p-2 text-right tabular-nums text-indigo-800">{formatINR(totals.closing_nbv || 0)}</td>
                  <td className="p-2 text-right tabular-nums text-amber-800 border-r border-slate-200">-{formatINR(totals.monthly_depreciation || 0)}</td>
                  <td className="p-2 text-right tabular-nums text-slate-900">{formatINR(totals.capital_employed || 0)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}

      {/* Data Source footer */}
      <Card className="p-4 bg-slate-50">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-600 font-bold mb-2">
          <FileText size={11} className="inline mr-1" /> Data Sources
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-1 text-xs text-slate-700">
          <div>• Stock Entries (IN/OUT/RETURN qty flows)</div>
          <div>• Inventory Batches (closing value · remaining_qty × rate)</div>
          <div>• Assets (Additions / Retirements)</div>
          <div>• Depreciation Snapshots (NBV waterfall · frozen)</div>
          <div>• Depreciation Adjustments (period-end corrections)</div>
        </div>
      </Card>
    </div>
  );
}


// ─── Drill-down tab ─────────────────────────────────────────────────────────

function WarehouseInventoryDrillTab({ row, source, from_period, to_period,
                                       asset_category, item_category, onClose }) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const meta = DRILL_SOURCES.find(s => s.key === source) || { label: source };

  useEffect(() => {
    setLoading(true);
    const params = { from_period, to_period, limit: 500 };
    if (asset_category) params.asset_category = asset_category;
    if (item_category)  params.item_category  = item_category;
    api.get(`/reports/warehouse-inventory/${row.warehouse_id}/drill/${source}`, { params })
      .then(r => setData(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load drill-down."))
      .finally(() => setLoading(false));
  }, [row.warehouse_id, source, from_period, to_period, asset_category, item_category]);

  return (
    <ViewOverlay onClose={onClose} testid="warehouse-inventory-drill-overlay">
      <Toaster richColors position="top-right" />
      <div className="p-6 space-y-4">
        <PageHeader
          eyebrow={`Drill-Down · ${row.warehouse_code}`}
          title={meta.label}
          subtitle={`${row.warehouse_name} · ${row.branch_name} · ${from_period} → ${to_period}`}
          actions={
            <button onClick={onClose}
                    className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900">
              <X size={16} className="inline mr-1" /> Close
            </button>
          }
        />
        {loading
          ? <div className="text-center text-slate-500 py-12">Loading source documents…</div>
          : data.length === 0
          ? <EmptyState title="No source documents in this scope" hint="Try widening the period or clearing the category filter." />
          : <Card className="overflow-x-auto">
              <DrillTable source={source} rows={data} />
            </Card>}
      </div>
    </ViewOverlay>
  );
}


function DrillTable({ source, rows }) {
  if (source === "stock_entries") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-stock">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Type</th><th className="text-left p-3">Item</th><th className="text-right p-3">Qty</th><th className="text-right p-3">Unit Rate</th><th className="text-left p-3">Source</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 text-xs">{r.entry_date}</td>
              <td className="p-3 text-xs">
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${
                  r.entry_type === "IN"     ? "bg-emerald-100 text-emerald-800" :
                  r.entry_type === "RETURN" ? "bg-emerald-100 text-emerald-800" :
                                              "bg-amber-100 text-amber-800"
                }`}>{r.entry_type}</span>
              </td>
              <td className="p-3 text-xs">
                <span className="font-mono font-bold text-[#F97316]">{r.item_code}</span>
                <span className="text-slate-400 ml-1">· {r.item_name}</span>
              </td>
              <td className="p-3 text-right tabular-nums font-bold">{r.quantity}</td>
              <td className="p-3 text-right tabular-nums">{formatINR(r.unit_rate || 0)}</td>
              <td className="p-3 text-[10px] text-slate-500">{r.source || r.ref_display || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "inventory_batches") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-batches">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Batch</th><th className="text-left p-3">Item</th><th className="text-right p-3">Remaining</th><th className="text-right p-3">Unit Rate</th><th className="text-right p-3">Value</th></tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const rate = r.landed_rate || r.unit_rate || r.rate || 0;
            const value = (r.remaining_qty || 0) * rate;
            return (
              <tr key={r.id} className="border-b border-slate-100">
                <td className="p-3 font-mono text-xs text-[#F97316]">{r.batch_code}</td>
                <td className="p-3 text-xs">
                  <span className="font-mono font-bold">{r.item_code}</span>
                  <span className="text-slate-400 ml-1">· {r.item_name}</span>
                </td>
                <td className="p-3 text-right tabular-nums font-bold">{r.remaining_qty}</td>
                <td className="p-3 text-right tabular-nums">{formatINR(rate)}</td>
                <td className="p-3 text-right tabular-nums font-bold text-emerald-700">{formatINR(value)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }
  if (source === "asset_additions" || source === "asset_retirements") {
    const isScrap = source === "asset_retirements";
    return (
      <table className="w-full text-sm" data-testid={isScrap ? "drill-table-scrap" : "drill-table-additions"}>
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Asset</th><th className="text-left p-3">Category</th><th className="text-left p-3">Purchase Date</th><th className="text-right p-3">Purchase Value</th><th className="text-left p-3">Status</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3">
                <span className="font-mono font-bold text-[#F97316]">{r.asset_code}</span>
                <span className="text-slate-400 ml-1 text-xs">· {r.name}</span>
              </td>
              <td className="p-3 text-xs">{r.category || "—"}</td>
              <td className="p-3 text-xs">{r.purchase_date || "—"}</td>
              <td className={`p-3 text-right tabular-nums font-bold ${isScrap ? "text-rose-700" : "text-emerald-700"}`}>
                {formatINR(r.purchase_value || 0)}
              </td>
              <td className="p-3 text-xs">
                <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${
                  r.status === "Scrapped" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"
                }`}>{r.status}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "depreciation_snapshots") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-snap">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Period</th><th className="text-left p-3">Asset</th><th className="text-right p-3">Opening NBV</th><th className="text-right p-3">Depreciation</th><th className="text-right p-3">Closing NBV</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold">{r.period_key}</td>
              <td className="p-3 text-xs">
                <span className="font-mono font-bold text-[#F97316]">{r.asset_code}</span>
                <span className="text-slate-400 ml-1">· {r.asset_name}</span>
              </td>
              <td className="p-3 text-right tabular-nums">{formatINR(r.opening_nbv || 0)}</td>
              <td className="p-3 text-right tabular-nums font-bold text-amber-800">-{formatINR(r.monthly_depreciation || 0)}</td>
              <td className="p-3 text-right tabular-nums">{formatINR(r.closing_nbv || 0)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  if (source === "depreciation_adjustments") {
    return (
      <table className="w-full text-sm" data-testid="drill-table-adj">
        <thead className="bg-slate-50 border-b text-[10px] uppercase tracking-wider font-bold text-slate-500">
          <tr><th className="text-left p-3">Period</th><th className="text-left p-3">Asset</th><th className="text-right p-3">Δ</th><th className="text-left p-3">Reason</th><th className="text-left p-3">By</th></tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.id} className="border-b border-slate-100">
              <td className="p-3 font-mono font-bold">{r.period_key}</td>
              <td className="p-3 text-xs">{r.asset_code || "—"}</td>
              <td className={`p-3 text-right tabular-nums font-bold ${r.delta >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                {r.delta >= 0 ? "+" : ""}{formatINR(r.delta)}
              </td>
              <td className="p-3 text-xs max-w-md truncate" title={r.reason}>{r.reason}</td>
              <td className="p-3 text-[10px] text-slate-500"><UserLabel value={r.posted_by} fallback="—" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  return null;
}


// ─── Small helpers ──────────────────────────────────────────────────────────

const inputCls = "w-full border border-slate-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#F97316]";

function FilterField({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

function KpiTile({ icon, label, value, tone = "slate" }) {
  const palettes = {
    "emerald":    "bg-emerald-50 border-emerald-200 text-emerald-900",
    "indigo":     "bg-indigo-50 border-indigo-200 text-indigo-900",
    "amber":      "bg-amber-50 border-amber-200 text-amber-900",
    "slate":      "bg-slate-50 border-slate-200 text-slate-900",
    "slate-bold": "bg-slate-900 border-slate-900 text-white",
  };
  const v = (value === null || value === undefined)
    ? "—"
    : `₹${Number(value).toLocaleString("en-IN")}`;
  return (
    <div className={`p-4 border ${palettes[tone]}`}
         data-testid={`warehouse-inventory-kpi-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold opacity-70">
        {icon} <span className="ml-1">{label}</span>
      </div>
      <div className="text-xl font-black tabular-nums mt-1">{v}</div>
    </div>
  );
}

function DrillCell({ value, onClick, count, tone = "slate", prefix = "" }) {
  const disabled = !count || count === 0;
  const palette = {
    "emerald": "text-emerald-700",
    "amber":   "text-amber-800",
    "rose":    "text-rose-700",
    "slate":   "text-slate-700",
  }[tone] || "text-slate-700";
  return (
    <td className={`p-2 text-right tabular-nums ${palette} ${disabled ? "" : "cursor-pointer hover:underline"}`}
        onClick={disabled ? undefined : onClick}
        data-testid={disabled ? undefined : `drill-cell-${tone}`}
        title={disabled ? "No documents in this scope" : `Click to see ${count} document(s)`}>
      {value
        ? <>{prefix}{formatINR(value)}{!disabled && <ArrowRight size={9} className="inline ml-1 opacity-40" />}</>
        : <span className="text-slate-300">—</span>}
    </td>
  );
}
